import type { CardTitle } from './ids';
import type { ComboRules } from './config';
import { richText, t, type TextKey } from '../i18n';
import { textPlain, textTagged, type TextEnv, type TextSpec } from '../i18n/text';

/**
 * **牌的说明（lore）** —— 它是全游戏文本组件系统（`i18n/text.ts`）的一个使用者。
 *
 * `Lore` 只是"按层面分段的 `TextSpec`"：
 *
 * 1. **层面 `LoreSection`**：`effect` / `strategy` / `counter` / `combo` / `hint`。
 * 2. **每层是一段 `TextSpec`**（`TextComponent[]`）：纯文本、牌引用（按 id）、
 *    术语（按语言查词）、动态值、嵌套键。颜色是系统的能力，不是 lore 特有的。
 *
 * ## 富文本的目的是"灵活"，不是"花哨"
 *
 * 最要紧的一条：**牌引用存 id**。改名/换语言时所有说明自动跟着变。
 * 颜色只是附带，且只支持颜色这一种样式。
 */

/** 说明的层面。**渲染顺序 = 这个数组的顺序** */
export const LORE_SECTIONS = ['effect', 'strategy', 'counter', 'combo', 'hint'] as const;
export type LoreSection = (typeof LORE_SECTIONS)[number];

/**
 * 固定的游戏术语。术语表在 i18n 的 `lore.term.*`（这一层不是 lore 专用，
 * 只是键名沿用了历史前缀）。
 */
export const LORE_TERMS = [
  'nearDeath',
  'lethal',
  'rescue',
  'interrupt',
  'turn',
  'drawPile',
  'discardPile',
  'hand',
  'combo',
  'pair',
  'triple',
  'fiveDifferent',
] as const;
export type LoreTerm = (typeof LORE_TERMS)[number];

/** 一张牌的说明，按层面分段。省略的层面不显示 */
export type Lore = Partial<Record<LoreSection, TextSpec>>;

/**
 * **内容层的文本环境。**
 *
 * 和 i18n 自己的环境只差一处：**牌引用**用调用方给的 `cardName`（内容层有注册表），
 * 而 i18n 自己没有注册表。术语和嵌套键都还是走 i18n。
 */
export function contentTextEnv(cardName: (id: CardTitle) => string): TextEnv {
  return {
    key: (key, params, rich) => (rich ? richText(key, params) : t(key, params)),
    card: cardName,
    term: (name) => t(('lore.term.' + name) as TextKey),
  };
}

/**
 * **插在某个层面之后**的一段额外 tagged text（不属于 `Lore` 的五个层面）。
 *
 * 现在唯一的用途：无效牌（Powerless）的"能用来打什么连击"其实读的是**房间村规**，
 * 不是这张牌自己的说明。所以那几段按当前房间开没开、动态插在"功能"之后。
 */
export interface LoreInjection {
  readonly after: LoreSection;
  readonly tagged: string;
}

function sectionHeading(section: LoreSection): string {
  return t(('lore.section.' + section) as TextKey);
}

/**
 * 把一个 `Lore` 转成 Pixi tagged text。每个层面渲染成 `<h>标题</h>` + 正文。
 *
 * `visible` 是玩家在设置里勾选的板块（`ui/settings.ts` 的 `loreSections`）；
 * 不给就全显示。注入段（`inject`）**跟它 `after` 的那个板块同进同出** ——
 * 关掉「功能」时，无效牌那几段「功能：一对」也必须跟着消失。
 */
export function loreToTagged(
  lore: Lore,
  cardName: (id: CardTitle) => string,
  inject?: LoreInjection,
  visible?: (section: LoreSection) => boolean,
): string {
  const env = contentTextEnv(cardName);
  const blocks: string[] = [];
  for (const section of LORE_SECTIONS) {
    if (visible !== undefined && !visible(section)) continue;
    const parts = lore[section];
    if (parts !== undefined && parts.length > 0) {
      blocks.push('<h>' + sectionHeading(section) + '</h>\n' + textTagged(parts, {}, env));
    }
    if (inject !== undefined && inject.after === section && inject.tagged !== '') {
      blocks.push(inject.tagged);
    }
  }
  return blocks.join('\n\n');
}

/** 同上的**纯文本**版本：给远端机器人 / 日志 / 无富文本的场合用 */
export function loreToPlainText(lore: Lore, cardName: (id: CardTitle) => string): string {
  const env = contentTextEnv(cardName);
  const blocks: string[] = [];
  for (const section of LORE_SECTIONS) {
    const parts = lore[section];
    if (parts === undefined || parts.length === 0) continue;
    blocks.push(sectionHeading(section) + '\n' + textPlain(parts, {}, env));
  }
  return blocks.join('\n\n');
}

/**
 * **无效牌额外显示的"连击规则"**（按房间村规是否启用逐条决定）。
 *
 * 标题形如"功能：一对"；正文是独立的 i18n 键（里面用 `term` 组件，会高亮）。
 * 它**不是** `LoreSection` 的一层，所以不塞进牌的 `lore` 数据里 ——
 * 同一张无效牌在不同房间显示不同规则。
 */
export function comboRuleExtras(combos: ComboRules): string {
  const rules: readonly { readonly enabled: boolean; readonly title: TextKey; readonly body: TextKey }[] = [
    { enabled: combos.twoOfAKind, title: 'lore.combo.pair.title', body: 'lore.combo.pair.body' },
    { enabled: combos.threeOfAKind, title: 'lore.combo.triple.title', body: 'lore.combo.triple.body' },
    {
      enabled: combos.fiveDifferent,
      title: 'lore.combo.fiveDifferent.title',
      body: 'lore.combo.fiveDifferent.body',
    },
  ];
  return rules
    .filter((rule) => rule.enabled)
    .map((rule) => '<h>' + t(rule.title) + '</h>\n' + richText(rule.body))
    .join('\n\n');
}

/** 某个层面有没有内容 —— 工具 / 测试用 */
export function hasLore(lore: Lore, section: LoreSection): boolean {
  const parts = lore[section];
  return parts !== undefined && parts.length > 0;
}
